import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { formatDate } from '@/lib/date';
import Modal from '@/components/base/Modal';
import DuyuruModal from './components/DuyuruModal';

interface AnnouncementRow {
  id: string;
  title: string;
  message: string | null;
  group_id: string | null;
  date: string | null;
  priority: string;
  visible: boolean;
  views: number;
  created_at: string;
}

interface GroupInfo {
  id: string;
  name: string;
  color: string | null;
}

const priorityMeta: Record<string, { label: string; color: string; icon: string; badge: string }> = {
  dusuk: { label: 'Düşük', color: 'gray', icon: 'ri-information-line', badge: 'bg-secondary-100 text-secondary-700' },
  orta: { label: 'Orta', color: 'amber', icon: 'ri-alert-line', badge: 'bg-amber-50 text-amber-600' },
  yuksek: { label: 'Yüksek', color: 'red', icon: 'ri-error-warning-line', badge: 'bg-red-50 text-red-600' },
};

export default function Duyurular() {
  const [announcements, setAnnouncements] = useState<AnnouncementRow[]>([]);
  const [groups, setGroups] = useState<GroupInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editAnn, setEditAnn] = useState<AnnouncementRow | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AnnouncementRow | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [
        { data: annData, error: annError },
        { data: groupsData, error: groupsError },
      ] = await Promise.all([
        supabase.from('zehrahoca_announcements').select('*').order('date', { ascending: false }),
        supabase.from('zehrahoca_groups').select('id, name, color').eq('active', true),
      ]);
      if (annError) throw annError;
      if (groupsError) throw groupsError;

      setAnnouncements((annData ?? []) as AnnouncementRow[]);
      setGroups((groupsData ?? []) as GroupInfo[]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const handleChange = () => fetchAll();
    window.addEventListener('zehrahoca:announcements-changed', handleChange);
    return () => window.removeEventListener('zehrahoca:announcements-changed', handleChange);
  }, [fetchAll]);

  const groupMap = useMemo(() => {
    const m: Record<string, GroupInfo> = {};
    groups.forEach((g) => { m[g.id] = g; });
    return m;
  }, [groups]);

  function handleNew() {
    setEditAnn(null);
    setModalOpen(true);
  }

  function handleEdit(a: AnnouncementRow) {
    setEditAnn(a);
    setModalOpen(true);
  }

  async function doDelete() {
    if (!confirmDelete) return;
    try {
      const { error: delError } = await supabase.from('zehrahoca_announcements').delete().eq('id', confirmDelete.id);
      if (delError) throw delError;
      setConfirmDelete(null);
      window.dispatchEvent(new Event('zehrahoca:announcements-changed'));
    } catch (err) {
      setError((err as Error).message || 'Silme işlemi başarısız oldu.');
      setConfirmDelete(null);
    }
  }

  async function copyToWhatsApp(a: AnnouncementRow) {
    const text = `📢 ${a.title}\n\n${a.message ?? ''}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(a.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      setError('Panoya kopyalanamadı.');
    }
  }

  if (loading) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-center py-20">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-500" />
        <p className="mt-4 text-sm text-foreground-500">Duyurular yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-7xl py-10">
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
          <i className="ri-error-warning-line text-2xl text-red-500" />
          <p className="mt-2 text-sm font-medium text-red-700">Duyurular yüklenirken bir hata oluştu.</p>
          <p className="mt-1 text-xs text-red-500">{error}</p>
          <button type="button" onClick={fetchAll} className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-600 cursor-pointer">
            <i className="ri-refresh-line" /> Yeniden Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Duyurular</h1>
          <p className="mt-1 text-sm text-foreground-600">Duyuru oluşturun ve öğrencilerinizle paylaşın.</p>
        </div>
        <button type="button" onClick={handleNew} className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-600 cursor-pointer">
          <i className="ri-add-line text-base" /> Yeni Duyuru Ekle
        </button>
      </div>

      {announcements.length === 0 ? (
        <div className="rounded-xl border border-background-200 bg-white p-10 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
            <i className="ri-megaphone-line text-2xl text-foreground-400" />
          </div>
          <p className="text-sm font-semibold text-foreground-700">Henüz duyuru bulunmuyor.</p>
          <p className="mt-1 text-xs text-foreground-500">İlk duyurunuzu oluşturarak başlayın.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {announcements.map((a) => {
            const p = priorityMeta[a.priority] ?? priorityMeta.dusuk;
            const group = groupMap[a.group_id ?? ''];
            return (
              <div
                key={a.id}
                className={`flex flex-col gap-4 rounded-xl border bg-white p-5 transition hover:shadow-sm sm:flex-row sm:items-start sm:justify-between ${
                  a.visible ? 'border-background-200' : 'border-background-200 opacity-70'
                }`}
              >
                <div className="flex flex-1 items-start gap-4">
                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${p.badge}`}>
                    <i className={`${p.icon} text-xl`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <h3 className="font-semibold text-foreground-950 text-sm">{a.title}</h3>
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${p.badge}`}>
                        {p.label}
                      </span>
                      {!a.visible && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-background-100 px-2 py-0.5 text-[11px] font-semibold text-foreground-500">
                          <i className="ri-eye-off-line" /> Gizli
                        </span>
                      )}
                    </div>
                    {a.message && (
                      <p className="text-sm text-foreground-600 whitespace-pre-wrap">{a.message}</p>
                    )}
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-foreground-400">
                      <span>{a.date ? formatDate(a.date) : '—'}</span>
                      {group && (
                        <span className="inline-flex items-center gap-1 font-semibold" style={{ color: group.color ?? '#6366f1' }}>
                          <i className="ri-team-line" /> {group.name}
                        </span>
                      )}
                      <span className="flex items-center gap-1"><i className="ri-eye-line" /> {a.views} görüntülenme</span>
                    </div>
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-1.5 sm:flex-col">
                  <button
                    type="button"
                    onClick={() => copyToWhatsApp(a)}
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-600 transition hover:bg-emerald-100 cursor-pointer"
                    title="WhatsApp'a kopyala"
                  >
                    <i className={copiedId === a.id ? 'ri-check-line' : 'ri-whatsapp-line'} />
                    {copiedId === a.id ? 'Kopyalandı' : 'WhatsApp'}
                  </button>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => handleEdit(a)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-background-100 text-foreground-600 transition hover:bg-background-200"
                      title="Düzenle"
                    >
                      <i className="ri-edit-line text-base" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(a)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 text-red-500 transition hover:bg-red-100"
                      title="Sil"
                    >
                      <i className="ri-delete-bin-line text-base" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <DuyuruModal open={modalOpen} onClose={() => setModalOpen(false)} announcement={editAnn} groups={groups} />

      <Modal
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="Duyuruyu Sil"
        subtitle="Bu işlem geri alınamaz."
      >
        <div className="space-y-5">
          <p className="text-sm text-foreground-700">
            <span className="font-semibold">{confirmDelete?.title}</span> duyurusunu silmek istediğinize emin misiniz?
          </p>
          <div className="flex items-center justify-end gap-3 border-t border-background-200 pt-5">
            <button
              type="button"
              onClick={() => setConfirmDelete(null)}
              className="rounded-lg px-4 py-2.5 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer whitespace-nowrap"
            >
              Vazgeç
            </button>
            <button
              type="button"
              onClick={doDelete}
              className="rounded-lg bg-red-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-red-600 cursor-pointer whitespace-nowrap"
            >
              Sil
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}